import { Component, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { debounceTime, map, shareReplay } from 'rxjs/operators';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { FirmwareMetadataService } from './firmware-metadata.service';
import { FirmwareWithMetadata, FirmwareType, getFirmwareKey } from './firmware-metadata.model';
import { SUGGESTED_FIRMWARE_TAGS, FACTORY_TAGS, FIRMWARE_PRODUCT_IDS, LOGGER_TYPES, INVERTER_SERIAL_PATTERNS, normalizeTag, isValidTag, sortTags } from './firmware-metadata.utils';

/**
 * State for firmware loading operation
 */
interface LoadState {
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string;
  firmware: FirmwareWithMetadata[];
}

/**
 * State for metadata save operation
 */
interface SaveState {
  status: 'idle' | 'saving' | 'success' | 'error';
  error?: string;
}

const getLabels = () => ({
  title: 'Firmware Metadata',
  loading: 'Loading...',
  // Filters
  filterByType: 'Filter by type',
  filterByTags: 'Filter by tags',
  searchPlaceholder: 'Search by version or reference...',
  filterByLoggerType: 'Filter by logger type',
  filterByModelFamily: 'Filter by model family',
  allLoggerTypes: 'All',
  allModelFamilies: 'All',
  // Edit form
  reference: 'Reference',
  referencePlaceholder: 'Alternative name...',
  tags: 'Tags',
  addTag: 'Add tag',
  notes: 'Notes',
  notesPlaceholder: 'Optional notes...',
  releaseDate: 'Release Date',
  releaseDatePlaceholder: 'Select release date...',
  save: 'Save',
  cancel: 'Cancel',
  deleteMetadata: 'Delete Metadata',
  allFieldsRequired: 'All fields are required. Please fill in reference, tags, release date and notes before saving.',
  // Status messages
  noMetadata: 'No metadata',
  noFirmware: 'No firmware found',
  savedSuccess: 'Metadata saved successfully',
  errorSaving: 'Error saving metadata. Please try again.',
  errorLoading: 'Error loading firmware',
  invalidReleaseDate: 'Invalid release date. Please enter a complete date and time.',
  missingLoggerType: 'Please select a logger type.',
  missingSerialPatterns: 'Please select at least one serial pattern.',
  missingReference: 'Please enter a reference.',
  missingTags: 'Please select at least one tag.',
  // Types
  logger: 'Logger',
  inverter: 'Inverter',
  battery: 'Battery',
  all: 'All',
  // Table headers
  version: 'Version',
  name: 'Name',
  type: 'Type',
  product: 'Product',
  // Device-specific fields
  loggerType: 'Logger Type',
  loggerTypePlaceholder: 'Select logger type...',
  serialPatterns: 'Serial Patterns',
  serialPatternsPlaceholder: 'Select serial patterns...',
  // Suggested tags labels
  suggestedTags: 'Suggested tags',
  factoryTags: 'Factory tags',
  operationalTags: 'Operational tags',
  customTags: 'Custom tags',
  selectedTags: 'Selected tags',
});

@CreateCustomBlockSizeType({})
@Component({
  selector: 'lib-firmware-metadata-editor',
  templateUrl: './firmware-metadata-editor.component.html',
  styleUrls: ['./firmware-metadata-editor.component.scss'],
})
export class FirmwareMetadataEditorComponent implements ICustomBlock, OnInit {
  @Input() uiFramework: 'material' | 'ionic' = 'material';
  @Input() refresh$ = new Subject<void>();

  // Labels i18n
  _labels = getLabels();
  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  // Tag groups
  readonly factoryTags = FACTORY_TAGS;
  readonly operationalTags = SUGGESTED_FIRMWARE_TAGS.filter(
    t => !(FACTORY_TAGS as readonly string[]).includes(t)
  );
  readonly suggestedTags = SUGGESTED_FIRMWARE_TAGS;

  // Device-specific options
  readonly loggerTypes = LOGGER_TYPES;
  readonly inverterSerialPatterns = INVERTER_SERIAL_PATTERNS;
  readonly inverterModelFamilies = [...new Set(INVERTER_SERIAL_PATTERNS.map(sp => sp.modelFamily))];

  // Load state - single source of truth for loading operation
  readonly loadState$ = new BehaviorSubject<LoadState>({
    status: 'idle',
    firmware: [],
  });

  // Save state - single source of truth for save operation
  readonly saveState$ = new BehaviorSubject<SaveState>({
    status: 'idle',
  });

  // User interaction state
  readonly selectedFirmware$ = new BehaviorSubject<FirmwareWithMetadata | null>(null);
  readonly filterType$ = new BehaviorSubject<FirmwareType | 'all'>('all');
  readonly filterTags$ = new BehaviorSubject<string[]>([]);
  readonly filterLoggerType$ = new BehaviorSubject<string>('');
  readonly filterModelFamily$ = new BehaviorSubject<string>('');
  readonly searchQuery$ = new BehaviorSubject<string>('');

  // Derived observable for all firmware from loadState
  readonly allFirmware$ = this.loadState$.pipe(
    map(state => state.firmware),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Filtered firmware list
  readonly filteredFirmware$ = combineLatest([
    this.allFirmware$,
    this.filterType$,
    this.filterTags$,
    this.filterLoggerType$,
    this.filterModelFamily$,
    this.searchQuery$.pipe(debounceTime(300)),
  ]).pipe(
    map(([firmware, type, tags, loggerType, modelFamily, query]) => {
      let result = firmware;

      // Filter by type
      result = this.firmwareMetadataService.filterByType(result, type);

      // Filter by tags
      result = this.firmwareMetadataService.filterByTags(result, tags);

      // Filter by logger type (match metadata.loggerType)
      if (loggerType) {
        result = result.filter(fw => fw.metadata?.loggerType === loggerType);
      }

      // Filter by model family (match metadata.serialPatterns against INVERTER_SERIAL_PATTERNS modelFamily)
      if (modelFamily) {
        const matchingLabels = INVERTER_SERIAL_PATTERNS
          .filter(sp => sp.modelFamily === modelFamily)
          .map(sp => sp.label);
        result = result.filter(fw =>
          fw.metadata?.serialPatterns?.some(sp => matchingLabels.includes(sp))
        );
      }

      // Filter by search query
      result = this.firmwareMetadataService.filterByReference(result, query);

      return result;
    })
  );

  // Edit form
  readonly editForm = new FormGroup({
    reference: new FormControl(''),
    tags: new FormControl<string[]>([]),
    loggerType: new FormControl<string>(''),
    serialPatterns: new FormControl<string[]>([]),
    notes: new FormControl(''),
    releaseDate: new FormControl<Date | null>(null),
  });

  // Custom tag input
  customTagInput = '';

  // Validation: tracks which fields have errors after a save attempt
  missingFields = new Set<string>();

  constructor(
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    private firmwareMetadataService: FirmwareMetadataService,
  ) { }

  ngOnInit(): void {
    this.loadAllFirmware();
  }

  /**
   * Load all firmware from all known products
   */
  async loadAllFirmware(): Promise<void> {
    this.loadState$.next({ status: 'loading', firmware: [] });

    try {
      const [allFirmware, metadataStore] = await Promise.all([
        this.firmwareMetadataService.loadAllProducts(),
        this.firmwareMetadataService.getMetadataMap(),
      ]);

      // Attach metadata to each firmware
      const enriched = allFirmware.map(fw => ({
        ...fw,
        metadata: metadataStore[getFirmwareKey(fw.productId, fw.id)],
      }));

      this.loadState$.next({ status: 'success', firmware: enriched });
    } catch (error) {
      console.error('Error loading firmware:', error);
      this.loadState$.next({
        status: 'error',
        error: error instanceof Error ? error.message : this.labels.errorLoading,
        firmware: [],
      });
    }
  }

  /**
   * Get product label by productId
   */
  getProductLabel(productId: string): string {
    const product = FIRMWARE_PRODUCT_IDS.find(p => p.id === productId);
    return product?.label ?? productId;
  }

  /**
   * Select a firmware for editing
   */
  selectFirmware(firmware: FirmwareWithMetadata): void {
    this.selectedFirmware$.next(firmware);

    // Convert timestamp to Date object for mat-datepicker
    const releaseDate = firmware.metadata?.releaseDate
      ? new Date(firmware.metadata.releaseDate)
      : null;

    // Populate form with existing metadata
    this.editForm.patchValue({
      reference: firmware.metadata?.reference ?? '',
      tags: firmware.metadata?.tags ?? [],
      loggerType: firmware.metadata?.loggerType ?? '',
      serialPatterns: firmware.metadata?.serialPatterns ?? [],
      notes: firmware.metadata?.notes ?? '',
      releaseDate,
    });
  }

  /**
   * Cancel editing
   */
  cancelEdit(): void {
    this.selectedFirmware$.next(null);
    this.editForm.reset();
    this.customTagInput = '';
    this.missingFields.clear();
    this.saveState$.next({ status: 'idle' });
  }

  /**
   * Clear error when user modifies the form
   */
  clearSaveError(): void {
    this.missingFields.clear();
    if (this.saveState$.value.status === 'error') {
      this.saveState$.next({ status: 'idle' });
    }
  }

  /**
   * Validate release date value
   * Returns timestamp if valid, undefined if empty, or throws error if invalid
   */
  private validateReleaseDate(value: Date | string | null | undefined): number | undefined {
    if (!value) {
      return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);
    const timestamp = date.getTime();

    if (isNaN(timestamp)) {
      throw new Error(this.labels.invalidReleaseDate || 'Invalid release date.');
    }

    return timestamp;
  }

  /**
   * Validate form fields before save. Returns true if valid.
   */
  private validateForm(firmware: FirmwareWithMetadata): boolean {
    this.missingFields.clear();

    const reference = this.editForm.value.reference?.trim();
    if (!reference) {
      this.missingFields.add('reference');
    }

    const tags = this.editForm.value.tags ?? [];
    if (tags.length === 0) {
      this.missingFields.add('tags');
    }

    if (firmware.firmwareType === 'logger') {
      const loggerType = this.editForm.value.loggerType?.trim();
      if (!loggerType) {
        this.missingFields.add('loggerType');
      }
    }

    if (firmware.firmwareType === 'inverter') {
      const serialPatterns = this.editForm.value.serialPatterns ?? [];
      if (serialPatterns.length === 0) {
        this.missingFields.add('serialPatterns');
      }
    }

    return this.missingFields.size === 0;
  }

  /**
   * Check if a field has a validation error
   */
  hasFieldError(field: string): boolean {
    return this.missingFields.has(field);
  }

  /**
   * Save metadata
   */
  async saveMetadata(): Promise<void> {
    const firmware = this.selectedFirmware$.value;
    if (!firmware) {
      return;
    }

    // Validate required fields
    if (!this.validateForm(firmware)) {
      // Build a user-friendly message listing missing fields
      const messages: string[] = [];
      if (this.missingFields.has('reference')) { messages.push(this.labels.missingReference); }
      if (this.missingFields.has('tags')) { messages.push(this.labels.missingTags); }
      if (this.missingFields.has('loggerType')) { messages.push(this.labels.missingLoggerType); }
      if (this.missingFields.has('serialPatterns')) { messages.push(this.labels.missingSerialPatterns); }

      this.saveState$.next({
        status: 'error',
        error: messages.join(' '),
      });
      return;
    }

    // Validate release date before starting save
    let releaseDate: number | undefined;
    try {
      releaseDate = this.validateReleaseDate(this.editForm.value.releaseDate);
    } catch {
      this.missingFields.add('releaseDate');
      this.saveState$.next({
        status: 'error',
        error: this.labels.invalidReleaseDate,
      });
      return;
    }

    // Set saving state
    this.saveState$.next({ status: 'saving' });

    try {
      // Build metadata object
      const metadata: {
        reference: string;
        tags: string[];
        loggerType?: string;
        serialPatterns?: string[];
        notes?: string;
        releaseDate?: number;
      } = {
        reference: this.editForm.value.reference?.trim() ?? '',
        tags: sortTags(this.editForm.value.tags ?? []),
      };

      // Include device-specific fields based on firmware type
      if (firmware.firmwareType === 'logger') {
        metadata.loggerType = this.editForm.value.loggerType?.trim() ?? '';
      }

      if (firmware.firmwareType === 'inverter') {
        metadata.serialPatterns = this.editForm.value.serialPatterns ?? [];
      }

      // Only include optional fields if they have values
      const notesValue = this.editForm.value.notes?.trim();
      if (notesValue) {
        metadata.notes = notesValue;
      }

      if (releaseDate !== undefined) {
        metadata.releaseDate = releaseDate;
      }

      await this.firmwareMetadataService.saveMetadata(firmware, metadata);

      // Set success state
      this.saveState$.next({ status: 'success' });

      // Reset to idle after showing success message
      setTimeout(() => {
        if (this.saveState$.value.status === 'success') {
          this.saveState$.next({ status: 'idle' });
        }
      }, 3000);

      // Reload firmware to get updated metadata
      await this.loadAllFirmware();

      // Close edit panel
      this.cancelEdit();
    } catch (error) {
      console.error('Error saving metadata:', error);
      this.saveState$.next({
        status: 'error',
        error: this.labels.errorSaving,
      });
    }
  }

  /**
   * Toggle a suggested tag
   */
  toggleTag(tag: string): void {
    const currentTags = this.editForm.value.tags ?? [];
    const index = currentTags.indexOf(tag);

    if (index >= 0) {
      currentTags.splice(index, 1);
    } else {
      currentTags.push(tag);
    }

    this.editForm.patchValue({ tags: [...currentTags] });
  }

  /**
   * Add a custom tag
   */
  addCustomTag(tagInput: string): void {
    const tag = normalizeTag(tagInput);

    if (!isValidTag(tag)) {
      return;
    }

    const currentTags = this.editForm.value.tags ?? [];
    if (!currentTags.includes(tag)) {
      currentTags.push(tag);
      this.editForm.patchValue({ tags: [...currentTags] });
    }

    this.customTagInput = '';
  }

  /**
   * Remove a tag
   */
  removeTag(tag: string): void {
    const currentTags = this.editForm.value.tags ?? [];
    const index = currentTags.indexOf(tag);

    if (index >= 0) {
      currentTags.splice(index, 1);
      this.editForm.patchValue({ tags: [...currentTags] });
    }
  }

  /**
   * Check if a tag is selected
   */
  isTagSelected(tag: string): boolean {
    return (this.editForm.value.tags ?? []).includes(tag);
  }

  /**
   * Set type filter and reset device-specific filters
   */
  setFilterType(type: FirmwareType | 'all'): void {
    this.filterType$.next(type);
    this.filterLoggerType$.next('');
    this.filterModelFamily$.next('');
  }

  /**
   * Toggle filter tag
   */
  toggleFilterTag(tag: string): void {
    const currentTags = this.filterTags$.value;
    const index = currentTags.indexOf(tag);

    if (index >= 0) {
      currentTags.splice(index, 1);
    } else {
      currentTags.push(tag);
    }

    this.filterTags$.next([...currentTags]);
  }

  /**
   * Check if a filter tag is active
   */
  isFilterTagActive(tag: string): boolean {
    return this.filterTags$.value.includes(tag);
  }

  /**
   * Get firmware type icon
   */
  getTypeIcon(type: FirmwareType): string {
    if (this.uiFramework === 'material') {
      return type === 'logger' ? 'router' : type === 'battery' ? 'battery_charging_full' : 'solar_power';
    }
    return type === 'logger' ? 'git-network-outline' : type === 'battery' ? 'battery-charging-outline' : 'sunny-outline';
  }

  /**
   * Get firmware type label
   */
  getTypeLabel(type: FirmwareType): string {
    switch (type) {
      case 'logger': return this.labels.logger;
      case 'battery': return this.labels.battery;
      case 'inverter': return this.labels.inverter;
      default: return this.labels.inverter;
    }
  }

  /**
   * Delete all metadata for the selected firmware
   */
  async deleteAllMetadata(): Promise<void> {
    const firmware = this.selectedFirmware$.value;
    if (!firmware) {
      return;
    }

    this.saveState$.next({ status: 'saving' });

    try {
      await this.firmwareMetadataService.deleteMetadata(firmware.productId, firmware.id);

      this.saveState$.next({ status: 'success' });

      setTimeout(() => {
        if (this.saveState$.value.status === 'success') {
          this.saveState$.next({ status: 'idle' });
        }
      }, 3000);

      await this.loadAllFirmware();
      this.cancelEdit();
    } catch (error) {
      console.error('Error deleting metadata:', error);
      this.saveState$.next({
        status: 'error',
        error: error instanceof Error ? error.message : this.labels.errorSaving,
      });
    }
  }
}
